import { createContext, useContext, useReducer, useEffect } from 'react'
import { authAPI } from '../services/api'
import toast from 'react-hot-toast'
import { t } from '../i18n'

const AuthContext = createContext()

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
}

function authReducer(state, action) {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true, error: null }
    
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      }
    
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      }
    
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      }
    
    case 'CLEAR_ERROR':
      return { ...state, error: null }
    
    default:
      return state
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('token')
    const user = localStorage.getItem('user')
    
    if (token && user) {
      try {
        const userData = JSON.parse(user)
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user: userData, token }
        })
      } catch (error) {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
      }
    } else {
      dispatch({ type: 'AUTH_FAILURE', payload: null })
    }
  }, [])

  const login = async (credentials) => {
    dispatch({ type: 'AUTH_START' })
    
    try {
      const response = await authAPI.login(credentials)
      const { accessToken, refreshToken, user } = response.data
      
      localStorage.setItem('token', accessToken)
      localStorage.setItem('refreshToken', refreshToken)
      localStorage.setItem('user', JSON.stringify(user))
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token: accessToken }
      })
      
      toast.success(t("Welcome back, {v0}!", { v0: user.fullName || user.username }))
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed'
      dispatch({ type: 'AUTH_FAILURE', payload: message })
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const pinLogin = async (credentials) => {
    dispatch({ type: 'AUTH_START' })
    
    try {
      const response = await authAPI.pinLogin(credentials)
      const { accessToken, refreshToken, user } = response.data
      
      localStorage.setItem('token', accessToken)
      localStorage.setItem('refreshToken', refreshToken)
      localStorage.setItem('user', JSON.stringify(user))
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token: accessToken }
      })
      
      toast.success(t("Welcome, {v0}!", { v0: user.fullName || user.username }))
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || 'PIN login failed'
      dispatch({ type: 'AUTH_FAILURE', payload: message })
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const logout = async () => {
    try {
      await authAPI.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      dispatch({ type: 'AUTH_LOGOUT' })
      toast.success(t("Logged out successfully"))
    }
  }

  const changePassword = async (passwords) => {
    try {
      await authAPI.changePassword(passwords)
      toast.success(t("Password changed successfully"))
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || 'Password change failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  // A session handed over from another domain (see /admin/sso).
  const adoptSession = ({ accessToken, refreshToken, user }) => {
    localStorage.setItem('token', accessToken)
    localStorage.setItem('refreshToken', refreshToken)
    localStorage.setItem('user', JSON.stringify(user))
    dispatch({ type: 'AUTH_SUCCESS', payload: { user, token: accessToken } })
  }

  // Forget this domain's session without signing out on the server.
  const forgetSession = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
  }

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  const value = {
    ...state,
    login,
    pinLogin,
    logout,
    adoptSession,
    forgetSession,
    changePassword,
    clearError
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}